// Dữ liệu mẫu cho Freight Cost + lưới Hãng × Tuyến: sinh xác định (hạt giống cố định) để có ~18.000 dòng giá lịch sử
// như bảng thật, thử cuộn ảo. Thay bằng TblFreightRate phía mua qua DataSource / worker api khi A-07 xong.
import { CARRIERS, PORTS, isVietnamPort } from '@hana/domain';
import { BOARD_TODAY } from './fixtures';
import { routeKey } from './costRates';
import type { CostBuy, CostRate } from './types';

function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const addDays = (iso: string, n: number): string => new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Hãng chủ lực = 8 hãng đầu (top job 12 tháng — mẫu). */
export const COST_CARRIERS: readonly string[] = CARRIERS.slice(0, 24).map((c) => c.code);
export const MAIN_CARRIERS: readonly string[] = COST_CARRIERS.slice(0, 8);
const POLS = ['VNSGN', 'VNHPH'] as const;
const SOURCES = ['Email', 'Excel', 'Portal hãng', 'Pricing nhập'] as const;
const VERSIONS = 26;

function build() {
  const r = rng(20261023);
  const pods = PORTS.filter((p) => !isVietnamPort(p.code)).slice(0, 45);
  const rates: CostRate[] = [];
  const buys: CostBuy[] = [];
  const serve = new Map<string, Set<string>>();
  for (const pol of POLS) {
    for (const pod of pods) {
      const rk = routeKey({ pol, pod: pod.code });
      const base = 400 + Math.round(r() * 3600);
      const set = new Set<string>();
      for (const car of COST_CARRIERS) {
        if (r() > 0.42) continue;
        set.add(car);
        const via = r() < 0.35 ? 'SGSIN' : null;
        const tt = 4 + Math.round(r() * 28);
        const k = 0.85 + r() * 0.3;
        // trạng thái hiện tại: ~62% còn hạn · 14% sắp hết · 16% hết hạn · 8% chưa có giá (bỏ dòng mới nhất)
        const roll = r();
        const first = roll < 0.62 ? 8 + Math.round(r() * 30) : roll < 0.76 ? Math.round(r() * 7) : -(1 + Math.round(r() * 20));
        const skipLatest = roll >= 0.92;
        if (skipLatest) continue; // ô "Chưa có giá": hãng chạy tuyến nhưng chưa có dòng giá
        for (let v = 0; v < VERSIONS; v++) {
          const id = `${rk}|${car}|${v}`;
          const price = Math.round(base * k * (1 + (r() - 0.5) * 0.12));
          const validEnd = addDays(BOARD_TODAY, first - v * 14);
          rates.push({
            id,
            pol,
            pod: pod.code,
            carrier: car,
            via,
            transitDays: tt,
            validFrom: addDays(validEnd, -14),
            validEnd,
            dem: [5, 7, 10, 14, 21][Math.floor(r() * 5)] as number,
            det: [3, 5, 7, 10][Math.floor(r() * 4)] as number,
            source: SOURCES[Math.floor(r() * SOURCES.length)] as string,
            quotes: Math.floor(r() * 6),
            jobs: Math.floor(r() * 9),
          });
          buys.push({ id, dc20: Math.round(price * 0.55), dc40: Math.round(price * 0.9), hc40: price, rf40: Math.round(price * 1.7), prevHc40: Math.round(price * (1 + (r() - 0.5) * 0.14)) });
        }
      }
      serve.set(rk, set);
    }
  }
  return { rates, buys, serve };
}

const built = build();
export const COST_RATES: readonly CostRate[] = built.rates;
export const COST_BUYS: readonly CostBuy[] = built.buys;
/** Hãng nào chạy tuyến nào (dòng cột "Tuyến theo dõi" của Services Map) */
export const COST_SERVE: ReadonlyMap<string, ReadonlySet<string>> = built.serve;
